'use client';

import { useEffect, useState } from 'react';
import SecondaryAvailability from './secondary-availability';
import { ModernAvailabilityProps } from './types';
import { useTranslations } from 'next-intl';
import { getSessionStorage } from '@cat-ecom/common/utils/storageUtils';
import { CatSkeleton } from 'components/blocks-components';
import { Conditional, useBreakpoint } from '@cat-ecom/pcc-components';
import PrimaryAvailability from '../primary-availability';
import { AVAIL_BY_DATE, AVAIL_BY_SOURCE } from '../constants';
import AvailabilityBySourceDrawer from 'components/availability-by-source-drawer';
import StoreAvailabilityWrapper from '../store-availability-wrapper/store-availability-wrapper';
import { PAGE_BREAKPOINTS } from 'constants/common';

const ModernAvailability = ({
  availabilityData,
  availabilityToolTipMessage,
  hoseAssemblyDetails = {
    hoseAssemblyIndicator: false,
    hoseAvailabilityLabel: null
  },
  hideLabel,
  sourcingDetails,
  isPickupEnabled,
  isDeliveryEnabled,
  showSourcingDetailToggle,
  address,
  locale,
  isLoading,
  availRemoveInterimStockFlag,
  storeAvailability,
  PCC_availRemoveInterimStockFlag,
  showAdditionalInfo,
  hideSourceAndDates,
  shouldWrap,
  hideToolTipIcon,
  availabilityByStoreLink,
  showPickupCutoffFlag,
  isPDPPage,
  pdpEstimatedDeliveryDateFlag,
  isLoggedIn,
  cutoffTimesData,
  deliveryAvailability,
  isPCC_PDP_headerUpdateMobileView,
  openInDrawer = false,
  isPccReleaseRemanReplacement = false,
  showAvailabilityAsBottomDrawer = false,
  partsDiagramMobileView = false
}: ModernAvailabilityProps) => {
  const [isSourcingDetailsActive, setIsSourcingDetailsActive] = useState(true);
  const t = useTranslations();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const { hoseAssemblyIndicator } = hoseAssemblyDetails;
  const shouldDisplaySecondaryAvailability =
    isSourcingDetailsActive && sourcingDetails?.length > 0;
  const sourcingDetailsKey = showSourcingDetailToggle
    ? AVAIL_BY_DATE
    : AVAIL_BY_SOURCE;
  const availabilityTypeShowToggle =
    getSessionStorage(sourcingDetailsKey) === 'true';
  const availabilityToggleSwitch =
    (availabilityTypeShowToggle && !showSourcingDetailToggle) ||
    (!availabilityTypeShowToggle && showSourcingDetailToggle);

  const shouldDisplayPrimaryAvailability = !(
    availRemoveInterimStockFlag && storeAvailability
  );
  const shouldDisplayAvailabilityBySource =
    hideLabel &&
    sourcingDetails?.length > 0 &&
    !hoseAssemblyIndicator &&
    availabilityToggleSwitch;

  useEffect(() => {
    const shouldShowSourcingDetails = () => {
      const contactDealerArray = availabilityData?.filter(
        ({ availabilityMessage }) =>
          availabilityMessage === t('CONTACT_YOUR_DEALER')
      );
      const inStockArray = availabilityData?.filter(
        ({ availabilityMessage }) =>
          availabilityMessage === t('AVAIL_STANDARD_POPOVER_INSTOCK')
      );
      if (hoseAssemblyIndicator) {
        return false;
      }
      if (
        (contactDealerArray?.length > 0 &&
          contactDealerArray.length === availabilityData.length) ||
        (inStockArray?.length > 0 &&
          inStockArray.length === availabilityData.length)
      ) {
        return false;
      }
      return !!sourcingDetails?.length;
    };
    setIsSourcingDetailsActive(shouldShowSourcingDetails());
  }, [availabilityData, hoseAssemblyIndicator, sourcingDetails, t]);

  return (
    <Conditional
      test={!isLoading}
      fallback={<CatSkeleton data-testid="loader-id" size="lg" />}
    >
      {shouldDisplayAvailabilityBySource && (
        <AvailabilityBySourceDrawer
          sourcingDetails={sourcingDetails}
          address={address}
          isPDPPage={isPDPPage}
          showAvailabilityAsBottomDrawer={showAvailabilityAsBottomDrawer}
          isPCC_PDP_headerUpdateMobileView={isPCC_PDP_headerUpdateMobileView}
        />
      )}
      <>
        {PCC_availRemoveInterimStockFlag ? (
          <StoreAvailabilityWrapper
            hideLabel={hideLabel}
            hoseAssemblyDetails={hoseAssemblyDetails}
            isLoading={isLoading}
            isDeliveryEnabled={isDeliveryEnabled}
            isPickupEnabled={isPickupEnabled}
            availabilityToolTipMessage={availabilityToolTipMessage}
            hideHeaderAndDeliveryOptions={false}
            sourcingDetails={sourcingDetails}
            shouldWrap={shouldWrap}
            address={address}
            locale={locale}
            hideToolTipIcon={hideToolTipIcon}
            availabilityByStoreLink={availabilityByStoreLink}
            storeAvailability={storeAvailability}
            showPickupCutoffFlag={showPickupCutoffFlag}
            isPDPPage={isPDPPage}
            pdpEstimatedDeliveryDateFlag={pdpEstimatedDeliveryDateFlag}
            isLoggedIn={isLoggedIn}
            cutoffTimesData={cutoffTimesData}
            isPccReleaseRemanReplacement={isPccReleaseRemanReplacement}
            openInDrawer={openInDrawer}
            partsDiagramMobileView={partsDiagramMobileView}
            deliveryAvailability={deliveryAvailability}
            pdpHeaderUpdateMobileViewFlag={isPCC_PDP_headerUpdateMobileView}
            isMD={isMD}
          />
        ) : (
          <Conditional test={shouldDisplayPrimaryAvailability}>
            <PrimaryAvailability
              hideLabel={hideLabel}
              availabilityToolTipMessage={availabilityToolTipMessage}
              isDeliveryEnabled={isDeliveryEnabled}
              isPickupEnabled={isPickupEnabled}
              availabilityInformation={availabilityData}
              hoseAssemblyDetails={hoseAssemblyDetails}
              showAdditionalInfo={showAdditionalInfo}
              address={address}
              hideToolTipIcon={hideToolTipIcon}
            />
          </Conditional>
        )}
        {shouldDisplaySecondaryAvailability && (
          <SecondaryAvailability
            showSourcingDetailToggle={showSourcingDetailToggle}
            sourcingDetails={sourcingDetails}
            availabilityData={availabilityData}
            address={address}
            hideSourceAndDates={hideSourceAndDates}
            isPDPPage={isPDPPage}
            isPCC_PDP_headerUpdateMobileView={isPCC_PDP_headerUpdateMobileView}
          />
        )}
      </>
    </Conditional>
  );
};

export default ModernAvailability;
